import { clsx } from "clsx"
import {
  type AnchorHTMLAttributes,
  type AriaRole,
  createContext,
  createElement,
  forwardRef,
  type ReactNode,
  useContext,
} from "react"

import { type SX, theme, type ThemeKeys } from "../../theme"
import styles from "./Text.module.css"

interface BaseTextProps {
  display?: "inline-flex"
  align?: "inherit" | "start" | "center" | "end"
  children?: ReactNode
  color?: ThemeKeys<"color">
  component?: Component
  noWrap?: boolean
  variant?: keyof typeof textVariantMapping
  noSelect?: boolean
  htmlFor?: string
  underline?: boolean
  highlight?: boolean
  highlightColor?: ThemeKeys<"color">
  highlightBorderRadius?: ThemeKeys<"borderRadius">
  sx?: SX
  /**
   * Standard HTML title attribute
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/title
   */
  title?: string
  hyphens?: boolean
  overflowWrap?: "anywhere"
  /**
   * Standard HTML id attribute
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id
   */
  id?: string
  /**
   * Standard ARIA role attribute
   * https://www.w3.org/TR/wai-aria-1.1/#role_definitions
   */
  role?: AriaRole
}

interface LinkProps extends BaseTextProps {
  href: string
  color?: "indigo11" | "purple11" | "tomato11" | "white" | "grey11" | "grey12"
  underline?: boolean
  inline?: boolean
  component?: "a"
  target?: AnchorHTMLAttributes<HTMLAnchorElement>["target"]
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>["rel"]
  onClick?: AnchorHTMLAttributes<HTMLAnchorElement>["onClick"]
}

interface NonLinkProps extends BaseTextProps {
  component?: Exclude<Component, "a">
  href?: never
  inline?: never
  onClick?: never
}

export type TextProps = NonLinkProps | LinkProps

export const Text = forwardRef<HTMLElement, TextProps>(
  function Text(props, ref) {
    const { hasParentText } = useContext(TextContext)

    const {
      display,
      href,
      align = "inherit",
      children,
      color = href ? "indigo11" : "grey12",
      noWrap = false,
      variant = "normal",
      component = getDefaultComponent(variant, hasParentText, href),
      noSelect = false,
      underline = false,
      highlight = false,
      highlightColor = "purple4",
      highlightBorderRadius = 4,
      sx,
      htmlFor,
      inline,
      title,
      hyphens,
      overflowWrap,
      ...otherProps
    } = props

    const element = createElement(
      component,
      {
        className: clsx(
          styles.text,
          display && styles[`display-${display}`],
          styles[`variant-${variant}`],
          styles[`align-${align}`],
          noWrap && styles.noWrap,
          noSelect && styles.noSelect,
          underline && styles.underline,
          highlight && styles.highlight,
          inline && styles.inline,
          hyphens && styles.hyphens,
          overflowWrap && styles[`overflowWrap-${overflowWrap}`],
          href && styles.link,
        ),
        style: {
          ...sx,
          color: theme.color[color],
          ...(highlight && {
            backgroundColor: theme.color[highlightColor],
            borderRadius: theme.borderRadius[highlightBorderRadius],
          }),
        },
        ref,
        htmlFor: htmlFor,
        title: title,
        ...(href ? { href, target: "_blank", rel: "noreferrer" } : {}),
        ...otherProps,
      },
      children,
    )

    return (
      <TextContext.Provider value={{ hasParentText: true }}>
        {element}
      </TextContext.Provider>
    )
  },
)

type Component =
  | "code"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "header"
  | "kbd"
  | "p"
  | "pre"
  | "span"
  | "label"
  | "a"

export const textVariantMapping = {
  normal: "p",
  bold: "p",
  italic: "p",
  small: "p",
  extraSmall: "p",
  smallBold: "p",
  smallItalic: "p",
  emphasized: "p",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  inherit: "p",
} as const

function getDefaultComponent(
  variant: keyof typeof textVariantMapping,
  hasParentText: boolean,
  href?: string,
): Component {
  if (href) return "a"

  // Given the above Component mapping, the only semantically valid nested Text is a span.
  if (hasParentText) return "span"

  return textVariantMapping[variant]
}

const TextContext = createContext({ hasParentText: false })
